import { useState, useCallback, useRef, useEffect, useMemo } from "react"
import { message } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import useSequentialExecution from "../DataLoading/useSequentialExecution"
import {
  STATISTIC_TYPE,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"

// Types
import { CodebookData } from "types/codebook.types"
import { QueryInstance } from "types/engine.types"
import {
  ExcelTab,
  ExcelTableData,
  TabSettings,
} from "types/ReportingDashboard/reporting.types"

/**
 * Props for useReportTableDisplay hook
 */
export interface UseReportTableDisplayProps {
  /** Data codebook for variable information */
  codebook: CodebookData
  /** Query instance for data operations */
  queryInstance: QueryInstance | null
  /** Current filter configuration */
  filter: string
  /** Auto-run script content */
  autoRunScriptContent: string
  /** Configuration for all tabs */
  tabsConfig: ExcelTab[]
  /** Callback when tabs configuration changes */
  onTabsConfigChange: (newTabsConfig: ExcelTab[]) => void
}

/**
 * Reference interface for individual table components
 */
export interface TableRef {
  /** Trigger script execution for the table */
  triggerRunScript: () => Promise<boolean>
  /** Check if table has data */
  hasData?: () => boolean
  /** Get table data for export */
  getTableData: (forExport?: boolean) => Promise<(string | number | null)[][]>
}

/**
 * Hook for managing report table display business logic
 */
export const useReportTableDisplay = ({
  filter,
  tabsConfig,
  onTabsConfigChange,
}: UseReportTableDisplayProps) => {
  // ========================================================================================
  // STATE AND REFS
  // ========================================================================================

  const tableRefsMap = useRef<Map<string, TableRef>>(new Map())
  const prevFilterRef = useRef<string>(filter)

  // Sequential execution system to prevent concurrent script runs
  const { requestExecution, isExecuting } = useSequentialExecution({
    delayBetweenExecutions: 100,
    onError: (tabKey, error, reason) => {
      console.error(`Error executing table ${tabKey} (${reason}):`, error)
    },
  })

  const [activeKey, setActiveKey] = useState<string | undefined>(
    tabsConfig && tabsConfig.length > 0 ? tabsConfig[0].key : undefined
  )

  // ========================================================================================
  // EFFECTS
  // ========================================================================================

  /**
   * Update active key when tabs configuration changes
   */
  useEffect(() => {
    if (tabsConfig && tabsConfig.length > 0) {
      if (!tabsConfig.find((tab) => tab.key === activeKey)) {
        setActiveKey(tabsConfig[0].key)
      }
    } else if (tabsConfig && tabsConfig.length === 0) {
      setActiveKey(undefined)
    }
  }, [tabsConfig, activeKey])

  // ========================================================================================
  // SEQUENTIAL EXECUTION SYSTEM
  // ========================================================================================

  /**
   * Handler for individual table execution requests
   */
  const handleExecutionRequest = useCallback(
    (tabKey: string, reason: string = "unknown"): void => {
      const executeFunction = async () => {
        const tableRef = tableRefsMap.current.get(tabKey)
        const tabConfig = (tabsConfig || []).find((tc) => tc.key === tabKey)

        if (
          tableRef &&
          typeof tableRef.triggerRunScript === "function" &&
          tabConfig
        ) {
          await tableRef.triggerRunScript()
        }
      }

      requestExecution(tabKey, executeFunction, reason)
    },
    [requestExecution, tabsConfig]
  )

  /**
   * Process all tables sequentially for filter changes or export
   * Now uses the same sequential execution hook for consistency
   */
  const processTablesSequentially = useCallback(
    async (forExport: boolean = false): Promise<void> => {
      if (isExecuting()) {
        console.warn("Already executing, skipping bulk processing")
        return
      }

      if (forExport) {
        // keyed message flow — notify.toast has no replace-in-place; keep antd message here
        message.loading({
          content: "Preparing all tables for export...",
          key: "excelExportPrepGlobal",
          duration: 0,
        })
      }

      try {
        // Process tables sequentially using the same hook
        for (const tabConfig of tabsConfig || []) {
          const tableRef = tableRefsMap.current.get(tabConfig.key)

          if (tableRef && typeof tableRef.triggerRunScript === "function") {
            let shouldRun = false

            if (forExport) {
              const hasData = tableRef.hasData ? tableRef.hasData() : false
              if (
                tabConfig.rowVariables?.length > 0 &&
                tabConfig.columnVariables?.length > 0 &&
                !hasData
              ) {
                shouldRun = true
              }
            } else {
              // Filter change
              if (
                tabConfig.rowVariables?.length > 0 &&
                tabConfig.columnVariables?.length > 0
              ) {
                shouldRun = true
              }
            }

            if (shouldRun) {
              const executeFunction = async () => {
                const processingMessageKey = `excelExportPrep-${tabConfig.key}`

                if (forExport) {
                  // keyed message flow — notify.toast has no replace-in-place; keep antd message here
                  message.loading({
                    content: `Processing table: ${tabConfig.label}...`,
                    key: processingMessageKey,
                    duration: 0,
                  })
                }

                try {
                  const success = await tableRef.triggerRunScript()

                  if (forExport) {
                    if (success) {
                      message.success({
                        content: `Table ${tabConfig.label} processed.`,
                        key: processingMessageKey,
                        duration: 2,
                      })
                    } else {
                      message.error({
                        content: `Failed to process table ${tabConfig.label}.`,
                        key: processingMessageKey,
                        duration: 2,
                      })
                    }
                  }
                } catch (error) {
                  console.error(
                    `Error processing table ${tabConfig.key}:`,
                    error
                  )
                  if (forExport) {
                    message.error({
                      content: `Failed to process table ${tabConfig.label}.`,
                      key: processingMessageKey,
                      duration: 2,
                    })
                  }
                }
              }

              // Use the same sequential execution system
              requestExecution(
                tabConfig.key,
                executeFunction,
                forExport ? "export_preparation" : "filter_change"
              )
            }
          } else {
            console.warn(
              `No valid tableRef or config for key: ${tabConfig.key}`
            )
          }
        }

        // Wait for all executions to complete
        while (isExecuting()) {
          await new Promise((resolve) => setTimeout(resolve, 50))
        }
      } catch (error) {
        console.error("Error during sequential table processing:", error)
        if (forExport) {
          const errorMessage =
            error instanceof Error ? error.message : String(error)
          message.error({
            content: `Error preparing tables: ${errorMessage}`,
            key: "excelExportPrepGlobal",
            duration: 3,
          })
        }
      } finally {
        if (forExport) {
          message.destroy("excelExportPrepGlobal")
        }
      }
    },
    [tabsConfig, requestExecution, isExecuting]
  )

  /**
   * Effect to handle filter changes
   */
  useEffect(() => {
    if (filter !== prevFilterRef.current) {
      processTablesSequentially(false)
      prevFilterRef.current = filter
    }
  }, [filter, processTablesSequentially])

  // ========================================================================================
  // EXPORT FUNCTIONALITY
  // ========================================================================================

  /**
   * Get Excel tables data for export
   */
  const getExcelTablesData = useCallback(async (): Promise<
    ExcelTableData[]
  > => {
    if (isExecuting()) {
      notify.toast.warning("Export is already in progress.")
      return []
    }

    await processTablesSequentially(true)

    try {
      const allTablesData: ExcelTableData[] = []
      const tabKeysOrder = (tabsConfig || []).map((tc) => tc.key)

      for (const tabKey of tabKeysOrder) {
        const tableRef = tableRefsMap.current.get(tabKey)
        const tabConfig = (tabsConfig || []).find((tc) => tc.key === tabKey)

        if (
          tableRef &&
          typeof tableRef.getTableData === "function" &&
          tabConfig
        ) {
          try {
            const tableData = await tableRef.getTableData(true)
            if (tableData && Array.isArray(tableData) && tableData.length > 0) {
              allTablesData.push({
                name: tabConfig.label,
                data: tableData,
                headers: [], // Add empty headers array for now
              })
            } else {
              allTablesData.push({
                name: tabConfig.label,
                data: [["No data available"]],
                headers: [],
              })
            }
          } catch (err) {
            console.error(
              `Error getting data from tab ${tabConfig.label} for export:`,
              err
            )
            allTablesData.push({
              name: `${tabConfig.label} (Error)`,
              data: [["Error fetching data"]],
              headers: [],
            })
          }
        } else {
          allTablesData.push({
            name: tabConfig?.label || `Tab ${tabKey}`,
            data: [["Table component not ready"]],
            headers: [],
          })
        }
      }
      return allTablesData
    } catch (error) {
      console.error(
        "Error collecting Excel tables data after processing:",
        error
      )
      notify.toast.error("Failed to collect all table data for export.")
      return []
    }
  }, [tabsConfig, processTablesSequentially, isExecuting])

  // ========================================================================================
  // TAB MANAGEMENT
  // ========================================================================================

  /**
   * Add a new tab
   */
  const addTab = useCallback((): void => {
    const currentTabs = tabsConfig || []
    const newKey = `reportTab-${Date.now()}-${currentTabs.length + 1}`
    const newLabel = `Table ${currentTabs.length + 1}`
    const newTab: ExcelTab = {
      key: newKey,
      label: newLabel,
      rowVariables: [],
      columnVariables: [],
      selectedOperators: [
        STATISTIC_TYPE.N,
        STATISTIC_TYPE.BIG_N,
        STATISTIC_TYPE.ROW_PERCENT,
        STATISTIC_TYPE.COL_PERCENT,
        STATISTIC_TYPE.INDEX,
        STATISTIC_TYPE.PERC_PERCENT,
      ],
      tableOperationType: TABLE_OPERATION_TYPE.XTAB,
      removedElements: {
        columns: [],
        rows: [],
      },
      persistentRows: [],
    }
    if (onTabsConfigChange) {
      onTabsConfigChange([...currentTabs, newTab])
    }
    setActiveKey(newKey)
  }, [tabsConfig, onTabsConfigChange])

  /**
   * Remove a tab
   */
  const removeTab = useCallback(
    (targetKey: string): void => {
      let newActiveKey: string | undefined = activeKey
      let lastIndex = -1
      const currentTabs = tabsConfig || []
      currentTabs.forEach((tab, i) => {
        if (tab.key === targetKey) lastIndex = i - 1
      })
      const newItems = currentTabs.filter((tab) => tab.key !== targetKey)
      if (newItems.length && targetKey === activeKey) {
        if (lastIndex >= 0 && lastIndex < newItems.length)
          newActiveKey = newItems[lastIndex].key
        else newActiveKey = newItems[0].key
      } else if (newItems.length === 0) {
        newActiveKey = undefined
      }
      tableRefsMap.current.delete(targetKey)
      if (onTabsConfigChange) {
        onTabsConfigChange(newItems)
      }
      setActiveKey(newActiveKey)
    },
    [tabsConfig, activeKey, onTabsConfigChange]
  )

  /**
   * Handle tab edit operations (add/remove)
   */
  const onEdit = useCallback(
    (
      targetKeyOrEvent: React.MouseEvent | React.KeyboardEvent | string,
      action?: string
    ): void => {
      if (action === "add") addTab()
      else if (action === "remove" && typeof targetKeyOrEvent === "string") {
        removeTab(targetKeyOrEvent)
      }
    },
    [addTab, removeTab]
  )

  /**
   * Handle tab settings changes
   */
  const handleTabSettingsChange = useCallback(
    (tabKeyToUpdate: string, newSettings: TabSettings): void => {
      const currentTabs = tabsConfig || []
      const updatedTabs = currentTabs.map((tab) =>
        tab.key === tabKeyToUpdate
          ? {
              ...tab,
              rowVariables: newSettings.rowVariables,
              columnVariables: newSettings.columnVariables,
              selectedOperators: newSettings.selectedOperators,
              tableOperationType: newSettings.tableOperationType,
              removedElements: newSettings.removedElements || {
                columns: [],
                rows: [],
              },
              persistentRows: newSettings.persistentRows || [],
            }
          : tab
      )
      if (onTabsConfigChange) {
        onTabsConfigChange(updatedTabs)
      }
    },
    [tabsConfig, onTabsConfigChange]
  )

  /**
   * Handle tab label changes
   */
  const updateTabLabel = useCallback(
    (tabKey: string, newLabel: string): void => {
      const currentTabs = tabsConfig || []
      const updatedTabs = currentTabs.map((tc) =>
        tc.key === tabKey
          ? { ...tc, label: newLabel.trim() || `Table ${tabKey}` }
          : tc
      )
      if (onTabsConfigChange) onTabsConfigChange(updatedTabs)
    },
    [tabsConfig, onTabsConfigChange]
  )

  /**
   * Register a table ref for a specific tab
   */
  const registerTableRef = useCallback(
    (tabKey: string, tableRef: TableRef | null): void => {
      if (tableRef) {
        tableRefsMap.current.set(tabKey, tableRef)
      } else {
        tableRefsMap.current.delete(tabKey)
      }
    },
    []
  )

  // ========================================================================================
  // MEMOIZED INITIAL SETTINGS
  // ========================================================================================

  /**
   * Memoize all initial settings to prevent unnecessary re-renders
   */
  const memoizedInitialSettings = useMemo(() => {
    const settingsMap = new Map<string, TabSettings>()

    tabsConfig.forEach((tabItem) => {
      settingsMap.set(tabItem.key, {
        rowVariables: tabItem.rowVariables,
        columnVariables: tabItem.columnVariables,
        selectedOperators: tabItem.selectedOperators,
        tableOperationType: tabItem.tableOperationType,
        removedElements: tabItem.removedElements || {
          columns: [],
          rows: [],
        },
        persistentRows: tabItem.persistentRows || [],
      })
    })

    return settingsMap
  }, [tabsConfig])

  return {
    // State
    activeKey,
    setActiveKey,
    memoizedInitialSettings,

    // Tab management
    addTab,
    removeTab,
    onEdit,
    updateTabLabel,

    // Settings management
    handleTabSettingsChange,

    // Execution management
    handleExecutionRequest,
    processTablesSequentially,
    isExecuting: isExecuting(),

    // Export functionality
    getExcelTablesData,

    // Ref management
    registerTableRef,
    tableRefsMap: tableRefsMap.current,

    // Test method
    testRef: (): boolean => true,
  }
}
